import React, { useRef, useState, useEffect } from "react";
import { useForm, singleChargeSubmit } from "./provider";
import { prettyDate } from "../../utils/formatters";
import { ArrowLeftIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

export const CardPaymentPage = (props) => {
  const { state, dispatch, chargeData, stripe, elements } = useForm();

  const handleSubmit = async (event) => {
    event.preventDefault();
    const resp = await singleChargeSubmit({ dispatch, state, chargeData, stripe, elements });

    if (resp.success) {
      props.goToNamedStep('success_page');
    }
    if (resp.error) {
      dispatch({ type: "SET_ERRORS", errors: { base: resp.error.response.data.error } });
    }
  };

  const handleBackClick = () => {
    dispatch({ type: "SET_OFFLINE_PAYMENT", offlinePayment: false });
    props.goToNamedStep("pick_payment_method");
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={handleBackClick}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 text-gray-500" />
        </button>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Review & Confirm</h2>
          <p className="text-sm text-gray-500">
            {state.offlinePayment ? 'Recording offline payment' : 'Card payment'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Selected Invoices */}
        <InvoiceList />

        {/* Payment Summary */}
        <Summary />

        {/* Error Message */}
        {state.errors && state.errors.base && (
          <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200 flex items-center gap-3">
            <ExclamationCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{state.errors.base}</p>
          </div>
        )}

        {/* Offline Payment Notes */}
        {state.offlinePayment && <OfflinePaymentNotes />}

        {/* Legal Copy */}
        <LegalCopy />

        {/* Submit Button */}
        <SubmitBtn />
      </form>
    </div>
  );
};

const InvoiceList = () => {
  const { state, setInvoices } = useForm();
  const { invoices, fund } = state;

  return (
    <div className="mb-6">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Invoices to Pay</p>
      <div className="space-y-2">
        {invoices.map((invoice) => (
          <label
            key={invoice.id}
            className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
              invoice.checked
                ? 'border-emerald-300 bg-emerald-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <input
              type="checkbox"
              checked={invoice.checked}
              onChange={() => setInvoices(invoice.id)}
              className="h-5 w-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${invoice.checked ? 'text-emerald-900' : 'text-gray-900'}`}>
                {invoice.title}
              </p>
              <p className={`text-xs ${invoice.checked ? 'text-emerald-600' : 'text-gray-500'}`}>
                Due: {prettyDate(invoice.due_date)}
              </p>
            </div>
            <div className="text-right">
              <p className={`text-sm font-semibold ${invoice.checked ? 'text-emerald-700' : 'text-gray-700'}`}>
                ${(invoice.amount_due_cents / 100).toFixed(2)}
                {fund.amount_currency !== "USD" && ` ${fund.amount_currency}`}
              </p>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
};

const OfflinePaymentNotes = () => {
  const { state, dispatch } = useForm();
  const { notes } = state;

  const handleChange = (e) => {
    dispatch({ type: "SET_NOTES", notes: e.target.value });
  };

  return (
    <div className="mt-6">
      <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
        Payment Notes
      </label>
      <p className="text-xs text-gray-500 mb-2">
        Add check number, reference number, or other details
      </p>
      <input
        id="notes"
        type="text"
        value={notes}
        onChange={handleChange}
        placeholder="e.g., Check #1234"
        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
      />
    </div>
  );
};

const Summary = () => {
  const { chargeData, state } = useForm();
  const { fund } = state;
  const currentChargeData = chargeData();
  const currency = fund.amount_currency !== "USD" ? fund.amount_currency : "";

  return (
    <div className="bg-gray-50 rounded-xl p-4">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Payment Summary</p>
      <div className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Subtotal</span>
          <span className="text-gray-900">
            {currentChargeData.amount > 0 ? `$${currentChargeData.amount.toFixed(2)}` : '$0.00'}
            {currency && ` ${currency}`}
          </span>
        </div>
        {!state.offlinePayment && currentChargeData.totalFee > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Processing Fee</span>
            <span className="text-gray-900">
              ${currentChargeData.totalFee}
              {currency && ` ${currency}`}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between pt-3 border-t border-gray-200">
          <span className="text-base font-semibold text-gray-900">Total</span>
          <span className="text-lg font-bold text-emerald-600">
            ${currentChargeData.total}
            {currency && ` ${currency}`}
          </span>
        </div>
      </div>
    </div>
  );
};

const SubmitBtn = () => {
  const { state, chargeData, stripe } = useForm();
  const { loading, offlinePayment } = state;
  const currentChargeData = chargeData();
  
  const sentinelRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // When sentinel is not visible (scrolled past), make button sticky
        setIsSticky(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '0px 0px 0px 0px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const btnLoading = !stripe || loading;
  const isDisabled = btnLoading || currentChargeData.amount < 1 || isNaN(currentChargeData.amount);

  const buttonText = offlinePayment
    ? `Record ${currentChargeData.amount > 0 ? '$' + currentChargeData.total : ''} Payment`
    : `Pay ${currentChargeData.amount > 0 ? '$' + currentChargeData.total : ''}`;

  const buttonElement = (
    <button
      type="submit"
      disabled={isDisabled}
      className={`w-full py-4 px-6 rounded-xl text-white font-semibold text-lg transition-all duration-200 flex items-center justify-center gap-2 ${
        isDisabled
          ? 'bg-gray-300 cursor-not-allowed'
          : offlinePayment
            ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-lg shadow-amber-500/25'
            : 'bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 shadow-lg shadow-emerald-500/25'
      }`}
    >
      {btnLoading ? (
        <>
          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>Processing...</span>
        </>
      ) : (
        <>
          <CheckCircleIcon className="w-5 h-5" />
          <span>{buttonText}</span>
        </>
      )}
    </button>
  );

  return (
    <>
      {/* Sentinel element - marks where the button naturally sits */}
      <div ref={sentinelRef} className="mt-6" />
      
      {/* Placeholder to maintain layout when button is sticky */}
      {isSticky && <div className="h-[60px]" />}
      
      {/* The actual button - either in flow or sticky */}
      {isSticky ? (
        <div className={`fixed bottom-0 left-0 right-0 ${state.has_sidebar ? 'md:left-64' : ''} p-4 bg-white border-t border-gray-200 shadow-lg z-50`}>
          <div className="max-w-xl mx-auto px-4">
            {buttonElement}
          </div>
        </div>
      ) : (
        buttonElement
      )}
    </>
  );
};

const LegalCopy = () => {
  const { state } = useForm();
  const { autoPay } = state;

  return (
    <p className="mt-4 text-xs text-gray-500 text-center">
      By confirming your {autoPay ? "subscription" : "transaction"}, you
      allow Unionly to charge your card for this amount{" "}
      {autoPay ? "and future payments for the same amount " : ""}in
      accordance with their{" "}
      <a className="text-emerald-600 hover:underline" href="/terms-of-use">
        terms
      </a>{" "}
      and{" "}
      <a className="text-emerald-600 hover:underline" href="/privacy-policy">
        privacy policy
      </a>.
    </p>
  );
};
